import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { fadeUp, scrollToId } from "@/lib/motion";
import { track } from "@/lib/analytics";

export default function Projects({ projects = [] }) {
  const [active, setActive] = useState(0);
  if (!projects.length) return null;
  const p = projects[active];

  return (
    <section id="projects" data-testid="projects-section" className="relative bg-navy text-white py-28 overflow-hidden noise">
      <div className="absolute -top-40 right-0 h-96 w-96 rounded-full bg-brand-blue/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div {...fadeUp} className="max-w-3xl">
          <span className="eyebrow">Selected work</span>
          <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            Systems we've built, <span className="text-gradient">shipped and run.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-300 leading-relaxed">
            A look at our own products. They show the kind of software we build for the businesses we work with.
          </p>
        </motion.div>

        <div className="mt-12 flex flex-wrap gap-2">
          {projects.map((pr, i) => (
            <button
              key={pr.id}
              data-testid={`project-tab-${pr.id}`}
              onClick={() => { setActive(i); track("project_interaction", { section: pr.id }); }}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                i === active ? "bg-brand-blue text-white shadow-lg shadow-brand-blue/30" : "border border-white/15 text-slate-300 hover:bg-white/5"
              }`}
            >
              {pr.name}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 grid lg:grid-cols-2 gap-10 items-center"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-brand-blue/30 to-brand-cyan/10 blur-2xl rounded-3xl" />
              <div className="relative overflow-hidden rounded-2xl border border-white/10" style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 92% 100%, 0 100%)" }}>
                {p.image ? (
                  <img src={p.image} alt={p.name} className="w-full h-[340px] object-cover" />
                ) : (
                  <div data-testid={`project-placeholder-${p.id}`} className="w-full h-[340px] flex flex-col items-center justify-center bg-gradient-to-br from-navy to-brand-deep">
                    <img src="/logos/icon.png" alt="" className="w-16 mb-5 opacity-90 drop-shadow-[0_0_24px_rgba(37,99,235,0.4)]" />
                    <div className="font-display text-2xl font-bold text-white">{p.name}</div>
                    <div className="mt-2 font-mono text-[0.68rem] uppercase tracking-[0.22em] text-slate-400">Live preview coming soon</div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent pointer-events-none" />
                <span className="absolute top-4 left-4 rounded-full bg-navy/80 backdrop-blur px-3 py-1 text-xs font-mono uppercase tracking-wider text-brand-cyan border border-white/10">
                  {p.badge}
                </span>
              </div>
            </div>

            <div>
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-brand-cyan">{p.type}</div>
              <h3 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">{p.name}</h3>
              <p className="mt-4 text-lg text-slate-300 leading-relaxed">{p.tagline}</p>
              <ul className="mt-6 space-y-3">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex items-center gap-3 text-slate-200">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-blue/20 text-brand-cyan">
                      <Check size={14} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollToId("quote")}
                data-testid="project-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                Build something like this <ArrowUpRight size={16} />
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
